import { Base } from '@studiometa/js-toolkit';
import { transition, nextFrame, addClass, removeClass } from '@studiometa/js-toolkit/utils';
/**
 * ListingTabs component
 */
export default class ListingTabs extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'ListingTabs',
    refs: ['wrapper', 'btn[]', 'indicator', 'progress', 'gradientLeft', 'gradientRight'],
    emits: ['changing'],
  };

  /**
   * Store to save the sizes of each btn.
   * @type {Array<{ x: number, width: number }>}
   */
  sizes = [];

  hasGradientLeft = false;

  hasGradientRight = true;

  /**
   * Mounted hook
   */
  mounted() {
    this.setSizes();

    const queryValues = new Set(Array.from(new URLSearchParams(window.location.search).values()));
    let currentIndex = 0;

    // If window has a GET ?query find the currentindex based on the query value
    if (window.location.search.length > 0) {
      currentIndex = this.$refs.btn.findIndex((btn) => {
        const { value } = btn.dataset;
        return queryValues.has(value);
      });

      if (currentIndex === -1) {
        currentIndex = 0;
      }
    }

    nextFrame(() => this.updateIndicator(currentIndex));
  }

  /**
   * Resized hook.
   */
  resized() {
    this.setSizes();
    nextFrame(() => this.updateIndicator(this.currentIndex));
  }

  /**
   * Set sizes
   */
  setSizes() {
    const origin = this.$refs.indicator.getBoundingClientRect();

    const wrapperBound = this.$refs.wrapper.getBoundingClientRect() || null;
    const lastBtnBound = this.$refs.btn?.at(-1)?.getBoundingClientRect() || null;

    if (wrapperBound && lastBtnBound) {
      const wrapperWidth =
        lastBtnBound.x - wrapperBound.x + lastBtnBound.width > wrapperBound.width
          ? lastBtnBound.x - wrapperBound.x + lastBtnBound.width
          : wrapperBound.width;
      this.$refs.indicator.style.width = `${wrapperWidth}px`;
    }

    this.$refs.btn.forEach((btn, index) => {
      const { width, x } = btn.getBoundingClientRect();
      this.sizes[index] = {
        x: x - origin.x,
        width,
      };
    });
  }

  /**
   * Switch tab on button click.
   * @this {TabsInterface}
   * @param  {Event}  event The click event object.
   * @param  {number} index The index of the clicked button.
   * @returns {void}
   */
  onBtnClick(event, index) {
    this.updateIndicator(index);
    this.currentIndex = index;

    nextFrame(() => {
      this.$emit('changing');
    });
  }

  /**
   * Update indicator position.
   * @param {number} index to use.
   */
  updateIndicator(index) {
    this.$refs.btn.forEach((element) => {
      element.classList.remove('text-gold');
    });
    this.$refs.btn[index].classList.add('text-gold');

    transition(
      this.$refs.progress,
      {
        to: {
          transform: `translateX(${this.sizes[index]?.x}px) scaleX(${this.sizes[index]?.width})`,
          width: '1px',
        },
      },
      'keep',
    );
  }

  /**
   * Toggle gradients
   */
  scrolled() {
    if (this.$refs.wrapper.scrollLeft > 0) {
      if (!this.hasGradientLeft) {
        this.hasGradientLeft = true;
        removeClass(this.$refs.gradientLeft, 'opacity-0');
      }
    } else if (this.hasGradientLeft) {
      this.hasGradientLeft = false;
      addClass(this.$refs.gradientLeft, 'opacity-0');
    }

    if (
      this.$refs.wrapper.scrollLeft + this.$refs.wrapper.clientWidth ===
      this.$refs.wrapper.scrollWidth
    ) {
      if (this.hasGradientRight) {
        this.hasGradientRight = false;
        addClass(this.$refs.gradientRight, 'opacity-0');
      }
    } else if (!this.hasGradientRight) {
      this.hasGradientRight = true;
      removeClass(this.$refs.gradientRight, 'opacity-0');
    }
  }
}
